/**
 * Página de Cambio de Contraseña
 * Se muestra cuando un usuario con contraseña temporal intenta iniciar sesión
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AuthPages.css';

function NewPasswordPage() {
  const navigate = useNavigate();
  const { user, pendingPasswordChallenge, confirmNewPassword, authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Redirigir si ya está autenticado o no hay desafío pendiente
  useEffect(() => {
    if (!authLoading) {
      if (user) {
        navigate('/');
      } else if (!pendingPasswordChallenge) {
        navigate('/login');
      }
    }
  }, [user, pendingPasswordChallenge, authLoading, navigate]);

  // Validar contraseña en tiempo real
  useEffect(() => {
    const password = formData.newPassword;
    setPasswordValidation({
      hasMinLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    });
  }, [formData.newPassword]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every((v) => v === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validaciones
    if (!formData.email || !formData.newPassword || !formData.confirmPassword) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    if (!isPasswordValid()) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const result = await confirmNewPassword(formData.newPassword, formData.email);

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Error al cambiar la contraseña');
      }
    } catch (err) {
      console.error('Error cambiando contraseña:', err);
      setError('Error al cambiar la contraseña. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Cambiar Contraseña</h1>
          <p className="auth-subtitle">Debes cambiar tu contraseña temporal antes de continuar</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
          </div>

          {/* Requisitos de contraseña */}
          <div className="password-requirements">
            <p className="requirements-title">La contraseña debe contener:</p>
            <ul>
              <li className={passwordValidation.hasMinLength ? 'valid' : ''}>
                Al menos 8 caracteres
              </li>
              <li className={passwordValidation.hasUppercase ? 'valid' : ''}>
                Al menos una letra mayúscula
              </li>
              <li className={passwordValidation.hasLowercase ? 'valid' : ''}>
                Al menos una letra minúscula
              </li>
              <li className={passwordValidation.hasNumber ? 'valid' : ''}>Al menos un número</li>
              <li className={passwordValidation.hasSpecialChar ? 'valid' : ''}>
                Al menos un carácter especial (!@#$%^&*)
              </li>
            </ul>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || !isPasswordValid()}
          >
            {loading ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default NewPasswordPage;
